import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, Calendar } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { EmbersBackdrop } from "../components/site/Atoms";
import SEO, { ORG_SCHEMA, breadcrumbSchema, faqSchema, SITE } from "../components/SEO";
import SocialFollow from "../components/SocialFollow";
import CounselingDisclaimer from "../components/CounselingDisclaimer";
import { findArticle, ARTICLES } from "../data/articles";
import { SEO_LANDING_PAGES } from "../data/seoLandingPages";

const Article = () => {
  const { slug } = useParams();
  const article = findArticle(slug);
  if (!article) return <Navigate to="/articles" replace />;

  const related = (article.relatedPaths || [])
    .map((p) => SEO_LANDING_PAGES.find((x) => x.path === p))
    .filter(Boolean);

  const schemas = [
    ORG_SCHEMA,
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Articles", path: "/articles" },
      { name: article.title, path: `/articles/${article.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.description,
      "datePublished": article.publishedAt,
      "dateModified": article.publishedAt,
      "author": { "@type": "Organization", "name": "Revival Fire Ministries Global" },
      "publisher": {
        "@type": "Organization",
        "name": "Revival Fire Ministries Global",
        "logo": { "@type": "ImageObject", "url": `${SITE}/logo512.png` },
      },
      "mainEntityOfPage": `${SITE}/articles/${article.slug}`,
    },
    ...(article.faqs?.length ? [faqSchema(article.faqs)] : []),
  ];

  return (
    <>
      <SEO
        title={article.title}
        description={article.description}
        path={`/articles/${article.slug}`}
        type="article"
        jsonLd={schemas}
      />
      <main className="relative" data-testid={`article-${article.slug}`}>
        <Navbar />

        <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 px-6 overflow-hidden">
          <EmbersBackdrop density={8} />
          <div className="relative max-w-3xl mx-auto">
            <nav className="text-xs tracking-[0.22em] uppercase text-[#D9D9D9]/55 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#C9A44C]">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/articles" className="hover:text-[#C9A44C]">Articles</Link>
            </nav>
            <div className="flex items-center gap-3 text-xs text-[#C9A44C] mb-4">
              <Calendar className="h-3.5 w-3.5" />
              <time dateTime={article.publishedAt}>{new Date(article.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>
              {article.tags?.map((t) => (
                <span key={t} className="text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full border border-[#C9A44C]/30 text-[#D9D9D9]/70">{t}</span>
              ))}
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F6F0E6] leading-[1.05]">{article.h1}</h1>
          </div>
        </section>

        <section className="px-6">
          <div className="max-w-3xl mx-auto bento-card !p-8 border-l-4 border-[#C9A44C]">
            <span className="glow-blob" />
            <div className="overline mb-3">In short</div>
            <p className="text-[#F6F0E6] text-lg leading-relaxed">{article.answer}</p>
          </div>
        </section>

        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto space-y-10">
            {article.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-4">{s.heading}</h2>
                {s.body.split("\n\n").map((p, j) => (
                  <p key={j} className="text-[#D9D9D9] leading-relaxed mb-3 whitespace-pre-line">{p}</p>
                ))}
              </section>
            ))}

            {article.scriptures?.length > 0 && (
              <section>
                {article.scriptures.map((sc, i) => (
                  <blockquote key={i} className="bento-card !p-5 my-4">
                    <span className="glow-blob" />
                    <div className="text-[#C9A44C] text-xs tracking-[0.22em] uppercase mb-2">{sc.ref}</div>
                    <p className="text-[#D9D9D9] italic">&ldquo;{sc.text}&rdquo;</p>
                  </blockquote>
                ))}
              </section>
            )}

            {article.counseling && <CounselingDisclaimer />}

            {article.faqs?.length > 0 && (
              <section>
                <h2 className="font-serif-display text-2xl text-[#F6F0E6] mb-4">Frequently asked questions</h2>
                <div className="space-y-3">
                  {article.faqs.map((f, i) => (
                    <details key={i} className="bento-card !p-5 group">
                      <span className="glow-blob" />
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[#F6F0E6]">
                        <span className="font-medium">{f.q}</span>
                        <ChevronRight className="h-4 w-4 text-[#C9A44C] group-open:rotate-90 transition-transform" />
                      </summary>
                      <p className="mt-3 text-[#D9D9D9]">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-12 px-6 border-t border-[#C9A44C]/10">
            <div className="max-w-3xl mx-auto">
              <div className="overline mb-4">Continue reading</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {related.map((r) => (
                  <Link key={r.path} to={r.path} className="group bento-card !p-5 flex items-center justify-between hover:-translate-y-0.5 transition-all">
                    <span className="glow-blob" />
                    <div>
                      <div className="overline mb-1">{r.kicker}</div>
                      <div className="text-[#F6F0E6]">{r.h1}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#C9A44C] group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <SocialFollow />

        <section className="py-16 px-6 text-center">
          <h2 className="font-serif-display text-3xl text-[#F6F0E6]">Need prayer right now?</h2>
          <p className="mt-3 text-[#D9D9D9]">Our intercessory team is ready to stand with you.</p>
          <Link to="/request-prayer" className="mt-6 btn-gold inline-flex !py-3 !px-6 !text-sm">
            Request Prayer <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        <Footer />
      </main>
    </>
  );
};

export const ArticlesIndex = () => {
  const schemas = [
    ORG_SCHEMA,
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Articles", path: "/articles" },
    ]),
  ];
  return (
    <>
      <SEO
        title="Articles | Revival Fire Ministries Global"
        description="Biblical articles on prayer, deliverance, healing, the prophetic, marriage, family, and revival. Read teaching from Revival Fire Ministries Global."
        path="/articles"
        jsonLd={schemas}
      />
      <main className="relative" data-testid="articles-index">
        <Navbar />
        <section className="relative pt-28 pb-16 px-6 overflow-hidden">
          <EmbersBackdrop density={10} />
          <div className="relative max-w-6xl mx-auto">
            <div className="overline mb-4">Articles</div>
            <h1 className="font-serif-display text-5xl text-[#F6F0E6]">Teaching that <span className="italic gold-text">feeds the fire</span></h1>
            <p className="mt-4 max-w-2xl text-[#D9D9D9]">Biblical articles on prayer, deliverance, healing, the prophetic, marriage, family, and revival.</p>
          </div>
        </section>
        <section className="px-6 pb-24">
          <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ARTICLES.map((a) => (
              <Link key={a.slug} to={`/articles/${a.slug}`} className="group bento-card !p-6 flex flex-col gap-3 hover:-translate-y-0.5 transition-all" data-testid={`article-card-${a.slug}`}>
                <span className="glow-blob" />
                <div className="flex items-center gap-2 text-xs text-[#C9A44C]">
                  <Calendar className="h-3 w-3" />
                  <time dateTime={a.publishedAt}>{new Date(a.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</time>
                </div>
                <h2 className="font-serif-display text-xl text-[#F6F0E6] leading-tight">{a.h1}</h2>
                <p className="text-sm text-[#D9D9D9]/85 line-clamp-3">{a.excerpt}</p>
                <div className="mt-auto text-[#C9A44C] text-xs inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read article <ChevronRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </section>
        <SocialFollow />
        <Footer />
      </main>
    </>
  );
};

export default Article;
